import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  InputAdornment,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  CloudUpload,
  PhotoCamera,
  ArrowBack,
  Person,
  Lock,
  Assignment,
  Business,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import axios from "axios";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { requestID, baseUrl } from "../common/utils";

// Validation Schema - removed dob requirement
const userSchema = yup.object({
  // dob: yup.string().required("Date of Birth is required."),
  email: yup.string().email("Invalid email address").required("Email is required."),
  firstName: yup.string().required("First Name is required."),
  lastName: yup.string().required("Last Name is required."),
  middleName: yup.string().optional(),
  password: yup.string().min(6, "Password must be at least 6 characters long").required("Password is required."),
  phone: yup.string().required("Phone is required.").matches(/^\d{11}$/, "Phone must be a 11-digit number"),
  residentialAddress: yup.string().required("Residential Address is required."),
});

const MerchantRegistration = () => {
  const history = useHistory();
  const [activeStep, setActiveStep] = useState(0);
  const [firstName, setFirstName] = useState<string>("");
  const [middleName, setMiddleName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  // Commented out Date of Birth state
  // const [dob, setDob] = useState<Dayjs | null>(dayjs("2022-04-17"));
  const [residentialAddress, setResidentialAddress] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [status, setStatus] = useState({ loading: false, error: false });
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMsg, setAlertMsg] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [stepErrors, setStepErrors] = useState<{[key: number]: boolean}>({});

  const steps = ['Personal Information', 'Account Setup', 'Upload Documents'];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(userSchema),
  });

  // Validate current step before proceeding
  const validateStep = async (step: number): Promise<boolean> => {
    let isValid = true;

    switch (step) {
      case 0:
        // Validate all personal information fields (removed dob from validation)
        isValid = await trigger(['firstName', 'lastName', 'email', 'phone', 'residentialAddress']);
        if (!firstName || !lastName || !email || !phone || !residentialAddress) {
          isValid = false;
        }
        break;
      case 1:
        // Validate password fields
        isValid = await trigger(['password']);
        if (!password || !confirmPassword || password !== confirmPassword) {
          isValid = false;
        }
        break;
      case 2:
        // File upload is now required
        if (!file) {
          isValid = false;
        }
        break;
      default:
        isValid = true;
    }

    setStepErrors(prev => ({...prev, [step]: !isValid}));
    return isValid;
  };

  const handleNext = async () => {
    const isValid = await validateStep(activeStep);
    if (isValid) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const onShowAlert = () => {
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 6000);
  };

  const onRegisterHandler = async () => {
    setStatus({ loading: true, error: false });
    // Commented out Date of Birth formatting
    // const formattedDate = dob ? dob.toISOString().split("T")[0] : null;
    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("firstName", firstName);
    formData.append("middleName", middleName);
    formData.append("lastName", lastName);
    formData.append("email", email);
    formData.append("phone", phone);
    // Commented out Date of Birth from form data
    // formData.append("dob", formattedDate || "");
    formData.append("residentialAddress", residentialAddress);
    formData.append("password", password);
    
    if (file) {
      formData.append("passport", file);
    }

    try {
      const response = await axios.post(
        baseUrl() + "users/registerMember_caas",
        formData
      );
      console.log(response);
      const data = await response.data;
      
      if (data.status === "true") {
        setStatus({ loading: false, error: false });
        setAlertMsg("Registration successful!");
        reset();
        onShowAlert();
        setTimeout(() => {
          history.push("/login");
        }, 3000);
      } else {
        setAlertMsg(data.message);
        onShowAlert();
        setStatus({ loading: false, error: true });
      }
    } catch (error) {
      console.error(error);
      setAlertMsg("Registration failed. Please try again.");
      setStatus({ loading: false, error: true });
      onShowAlert();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    } else {
      setFile(null);
    }
  };

  const isMatched = confirmPassword.length > 0 && confirmPassword !== password;

  // Check if current step is complete (removed dob check)
  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!(firstName && lastName && email && phone && residentialAddress && 
                 !errors.firstName && !errors.lastName && !errors.email && !errors.phone && !errors.residentialAddress);
      case 1:
        return !!(password && confirmPassword && !errors.password && !isMatched);
      case 2:
        return !!file; // File upload is now required - step only complete when file is uploaded
      default:
        return false;
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="First Name *"
                variant="outlined"
                error={!!errors.firstName}
                {...register("firstName")}
                helperText={errors.firstName?.message}
                value={firstName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFirstName(event.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Middle Name"
                variant="outlined"
                error={!!errors.middleName}
                {...register("middleName")}
                value={middleName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMiddleName(event.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Last Name *"
                variant="outlined"
                error={!!errors.lastName}
                {...register("lastName")}
                helperText={errors.lastName?.message}
                value={lastName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setLastName(event.target.value);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Email Address *"
                variant="outlined"
                value={email}
                error={!!errors.email}
                {...register("email")}
                helperText={errors.email?.message}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(event.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number *"
                variant="outlined"
                error={!!errors.phone}
                {...register("phone")}
                helperText={errors.phone?.message || "Preferably WhatsApp"}
                value={phone}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPhone(event.target.value);
                }}
              />
            </Grid>

            {/* Commented out Date of Birth field */}
            {/* <Grid item xs={12} sm={6}>
              <DatePicker
                sx={{ width: "100%" }}
                label="Date Of Birth *"
                value={dob}
                slotProps={{
                  textField: {
                    error: !!errors.dob,
                    helperText: errors.dob?.message,
                  },
                }}
                onChange={(newValue: any) => setDob(newValue)}
              />
            </Grid> */}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Residential Address *"
                variant="outlined"
                error={!!errors.residentialAddress}
                {...register("residentialAddress")}
                helperText={errors.residentialAddress?.message}
                value={residentialAddress}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setResidentialAddress(event.target.value);
                }}
                multiline
                rows={3}
              />
            </Grid>

            {stepErrors[0] && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 1 }}>
                  Please complete all required fields (*) before proceeding.
                </Alert>
              </Grid>
            )}
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Account Security
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password *"
                type="password"
                variant="outlined"
                error={!!errors.password}
                {...register("password")}
                helperText={errors.password?.message}
                value={password}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(event.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirm Password *"
                type="password"
                variant="outlined"
                error={isMatched}
                helperText={isMatched ? "Passwords do not match" : ""}
                value={confirmPassword}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setConfirmPassword(event.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {stepErrors[1] && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 1 }}>
                  Please ensure passwords match and meet the requirements.
                </Alert>
              </Grid>
            )}
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" gutterBottom>
                Document Upload
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please upload your passport photograph for identification purposes.
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                variant="outlined" 
                sx={{ 
                  border: '2px dashed',
                  borderColor: file ? 'success.main' : 'primary.light',
                  backgroundColor: 'grey.50',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <CloudUpload sx={{ fontSize: 48, color: file ? 'success.main' : 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom color={file ? 'success.main' : 'primary'}>
                    {file ? 'Document Uploaded Successfully' : 'Upload Passport Photograph *'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Supported formats: JPG, PNG, GIF • Max size: 5MB
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant={file ? "outlined" : "contained"}
                      component="label"
                      startIcon={<PhotoCamera />}
                      color={file ? "success" : "primary"}
                    >
                      {file ? 'Change File' : 'Choose File *'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {file && (
                      <Typography variant="body2" color="success.main">
                        ✓ {file.name}
                      </Typography>
                    )}
                  </Box>
                  
                  {!file && (
                    <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
                      * This step is required to complete your registration.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {stepErrors[2] && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>
                  Please upload a passport photograph to complete your registration.
                </Alert>
              </Grid>
            )}
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" elevation={0} color="primary">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => history.goBack()}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
            <Business />
          </Avatar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Merchant Registration
          </Typography>
          <Button color="inherit" onClick={() => history.push("/login")}>
            Already have an account? Login
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={10} lg={8}>
            <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom align="center" color="primary" fontWeight="bold">
                Create Merchant Account
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                Complete the following steps to register as a merchant
              </Typography>

              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label, index) => (
                  <Step key={label} completed={isStepComplete(index)}>
                    <StepLabel 
                      error={stepErrors[index]}
                      sx={{
                        '& .MuiStepLabel-label': {
                          fontWeight: activeStep === index ? 'bold' : 'normal',
                        }
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {showAlert && (
                <Alert 
                  severity={status.error ? "error" : "success"} 
                  sx={{ mb: 3 }}
                  onClose={() => setShowAlert(false)}
                >
                  {alertMsg}
                  {!status.error && " Please check your email for registration details."}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit(onRegisterHandler)}>
                {getStepContent(activeStep)}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    onClick={activeStep === 0 ? () => history.goBack() : handleBack}
                    disabled={status.loading}
                    variant="outlined"
                    startIcon={<ArrowBack />}
                  >
                    {activeStep === 0 ? 'Cancel' : 'Back'}
                  </Button>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {activeStep < steps.length - 1 ? (
                      <Button
                        onClick={handleNext}
                        variant="contained"
                        disabled={status.loading || !isStepComplete(activeStep)}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        disabled={status.loading || isMatched || !file}
                        variant="contained"
                        size="large"
                        startIcon={status.loading ? <CircularProgress size={20} /> : <Assignment />}
                        sx={{ minWidth: 200 }}
                      >
                        {status.loading ? 'Processing...' : 'Complete Registration'}
                      </Button>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Fields marked with * are required
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default MerchantRegistration;